import { ExpandMore } from '@mui/icons-material'
import type { ReactNode } from 'react'
import React from 'react'
import styled from 'styled-components'
import { accents } from 'sutra/tokens/colors'

import type { ChartTypePickerSectionSpec } from 'features/charts-v2/components/ChartTypePicker/ChartTypePicker'
import { ChartTypePicker } from 'features/charts-v2/components/ChartTypePicker/ChartTypePicker'
import {
  CHART_TYPE_CONFIGS_V2,
  type ChartPreset,
  ChartTypeV2,
} from 'features/charts-v2/types/chart-types'

/** What the trigger button shows when the tile holds no chart at all — a
 *  dashboards-v2-1 widget/text tile has no chart preset to resolve a caption
 *  or label from, so it supplies its own. Absent for every chart caller,
 *  which derives caption/label from the resolved preset as usual. */
export interface ChartTypeSelectorSelectionOverride {
  caption: string
  label: string
  icon?: ReactNode
}

interface ChartTypeSelectorProps {
  sections: readonly ChartTypePickerSectionSpec[]
  selectedChartType?: ChartTypeV2
  selectedPresetId?: string | null
  onChartTypeChange: (chartType: ChartTypeV2) => void
  onPresetSelect?: (preset: ChartPreset | null) => void
  selectionOverride?: ChartTypeSelectorSelectionOverride
}

export const ChartTypeSelector: React.FC<ChartTypeSelectorProps> = ({
  sections,
  selectedChartType,
  selectedPresetId,
  onChartTypeChange,
  onPresetSelect,
  selectionOverride,
}) => {
  // Get current chart type config (fallback label when no preset is resolved)
  const currentChartConfig =
    CHART_TYPE_CONFIGS_V2.find((config) => config.id === selectedChartType) ??
    CHART_TYPE_CONFIGS_V2[0]!

  return (
    <ChartTypePicker
      sections={sections}
      selectedChartType={selectedChartType}
      selectedPresetId={selectedPresetId}
      onChartTypeChange={onChartTypeChange}
      onPresetSelect={onPresetSelect}
      trigger={({ isOpen, currentPreset, currentPresetGroup }) => {
        const icon = selectionOverride?.icon ?? currentPreset?.icon
        const caption = selectionOverride
          ? selectionOverride.caption
          : currentPresetGroup &&
            (currentPresetGroup.key === 'freeform'
              ? 'Freeform'
              : `Template > ${currentPresetGroup.label}`)
        const label =
          selectionOverride?.label ??
          (currentPreset ? currentPreset.label : currentChartConfig.label)

        return (
          <SelectorButton $isOpen={isOpen}>
            {icon && <IconSlot>{icon}</IconSlot>}
            <LabelColumn>
              {caption && <GroupCaption>{caption}</GroupCaption>}
              <SelectedLabel>{label}</SelectedLabel>
            </LabelColumn>
            <ChevronDown />
          </SelectorButton>
        )
      }}
    />
  )
}

const SelectorButton = styled.button<{ $isOpen: boolean }>`
  padding: 6px 6px;
  font-size: 14px;
  gap: 8px;
  font-weight: 500;
  min-height: 44px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: ${({ $isOpen }) =>
    $isOpen ? accents.reflectionBlue[100] : '#ffffff'};
  color: #374151;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background-color: ${accents.reflectionBlue[50]};
  }
`

const IconSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 100%;
`

const LabelColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`

const GroupCaption = styled.span`
  font-size: 10px;
  color: #6b7280;
  text-align: left;
`

const SelectedLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  overflow-wrap: break-word;
  word-break: break-word;
`

const ChevronDown = styled(ExpandMore)`
  font-size: 16px;
`
